import { Button } from 'primereact/button';
import { Menu as PrimeMenu } from 'primereact/menu';
import React, { useRef } from 'react';

import { useMindmap } from '../use-mindmap';
import { ManipulationActions } from '../use-mindmap-manipulation';
import { Menu, MenuVariantElementProps } from './use-tool-bar';

const buttonClasses = 'p-button-text';

const menu1Large = ({ hook }: MenuVariantElementProps) => {
  const values = hook();
  if (values?.type == 'node-manipulation') {
    const {
      actions: {
        onDeleteClick,
        onAddChildClick,
        onAddTopSiblingClick,
        onAddBottomSiblingClick,
        addBottomSiblingSelectable,
        addTopSiblingSelectable,
        addChildSelectable,
        deleteSelectable,
      },
    } = values;

    return (
      <>
        <div style={{ gridColumn: '1 / 2', gridRow: '1 / 2' }}>
          <Button
            onClick={onAddTopSiblingClick}
            label="Nachbarsknoten"
            icon="pi pi-angle-up"
            className={buttonClasses}
            disabled={addTopSiblingSelectable}
          />
        </div>
        <div style={{ gridColumn: '2 / 3', gridRow: '1 / 2' }}>
          <Button
            onClick={onAddChildClick}
            label="Unterknoten"
            icon="pi pi-angle-right"
            className={buttonClasses}
            disabled={addChildSelectable}
          />
        </div>
        <div style={{ gridColumn: '1 / 2', gridRow: '2 / 3' }}>
          <Button
            onClick={onAddBottomSiblingClick}
            label="Nachbarsknoten"
            icon="pi pi-angle-down"
            className={buttonClasses}
            disabled={addBottomSiblingSelectable}
          />
        </div>
        <div style={{ gridColumn: '2 / 3', gridRow: '2 / 3' }}>
          <Button
            onClick={onDeleteClick}
            label="Knoten löschen"
            icon="pi pi-times"
            className={`${buttonClasses} p-button-danger`}
            disabled={deleteSelectable}
          />
        </div>
      </>
    );
  }
  throw new Error('Unknown or missing hook or hook type.');
};

const menu1Medium = ({ hook }: MenuVariantElementProps) => {
  const values = hook();
  if (values?.type == 'node-manipulation') {
    const {
      actions: {
        onDeleteClick,
        onAddChildClick,
        onAddTopSiblingClick,
        onAddBottomSiblingClick,
        addBottomSiblingSelectable,
        addTopSiblingSelectable,
        addChildSelectable,
        deleteSelectable,
      },
    } = values;

    return (
      <>
        <div style={{ gridColumn: '1 / 2', gridRow: '1 / 2' }}>
          <Button
            onClick={onAddTopSiblingClick}
            label="Knoten"
            icon="pi pi-angle-up"
            className={buttonClasses}
            disabled={addTopSiblingSelectable}
          />
        </div>
        <div style={{ gridColumn: '2 / 3', gridRow: '1 / 2' }}>
          <Button
            onClick={onAddChildClick}
            label="Knoten"
            icon="pi pi-angle-right"
            className={buttonClasses}
            disabled={addChildSelectable}
          />
        </div>
        <div style={{ gridColumn: '1 / 2', gridRow: '2 / 3' }}>
          <Button
            onClick={onAddBottomSiblingClick}
            label="Knoten"
            icon="pi pi-angle-down"
            className={buttonClasses}
            disabled={addBottomSiblingSelectable}
          />
        </div>
        <div style={{ gridColumn: '2 / 3', gridRow: '2 / 3' }}>
          <Button
            onClick={onDeleteClick}
            label="Löschen"
            icon="pi pi-times"
            className={buttonClasses}
            disabled={deleteSelectable}
          />
        </div>
      </>
    );
  }
  throw new Error('Unknown or missing hook or hook type.');
};

const menu1Small = ({ hook }: MenuVariantElementProps) => {
  const values = hook();
  if (values?.type == 'node-manipulation') {
    const {
      actions: {
        onDeleteClick,
        onAddChildClick,
        onAddTopSiblingClick,
        onAddBottomSiblingClick,
        addBottomSiblingSelectable,
        addTopSiblingSelectable,
        addChildSelectable,
        deleteSelectable,
      },
      menuRef,
    } = values;

    const items = [
      {
        label: 'Nachbarsknoten',
        icon: 'pi pi-angle-up',
        command: onAddTopSiblingClick,
        disabled: addTopSiblingSelectable,
      },
      {
        label: 'Nachbarsknoten',
        icon: 'pi pi-angle-down',
        command: onAddBottomSiblingClick,
        disabled: addBottomSiblingSelectable,
      },
      {
        label: 'Unterknoten',
        icon: 'pi pi-angle-right',
        command: onAddChildClick,
      },
    ];

    const onMenuClick = (event: any) => {
      // @ts-ignore
      menuRef.current.toggle(event);
    };

    return (
      <>
        <div style={{ gridColumn: '1 / 2', gridRow: '1 / 2' }}>
          <PrimeMenu model={items} ref={menuRef} popup id="node_manipulation_popup_menu" />
          <Button
            label="Knoten"
            icon="pi pi-plus"
            className={buttonClasses}
            disabled={addChildSelectable}
            onClick={onMenuClick}
            aria-controls="node_manipulation_popup_menu"
            aria-haspopup
          />
        </div>
        <div style={{ gridColumn: '1 / 2', gridRow: '2 / 3' }}>
          <Button
            label="Löschen"
            icon="pi pi-cross"
            onClick={onDeleteClick}
            className={`${buttonClasses} p-button-danger`}
            disabled={deleteSelectable}
          />
        </div>
      </>
    );
  }
  throw new Error('Unknown or missing hook or hook type.');
};

export interface NodeManipulationHookValue {
  type: 'node-manipulation';
  actions: ManipulationActions;
  menuRef: React.MutableRefObject<null>;
}

export const nodeManipulationMenu: Menu = {
  id: 'menu-manipulation',
  variants: [
    { Element: menu1Small, priority: 10 },
    { Element: menu1Medium, priority: 10 },
    { Element: menu1Large, priority: 10 },
  ],
  currentVariant: 2,
  hook: () => {
    const menuRef = useRef(null);
    const {
      manipulation: { actions },
    } = useMindmap();

    return {
      type: 'node-manipulation',
      actions,
      menuRef,
    };
  },
  tempHook: () => ({
    type: 'node-manipulation',
    actions: {
      addBottomSiblingSelectable: false,
      addChildSelectable: false,
      addTopSiblingSelectable: false,
      deleteSelectable: false,
      addAttachementSelectable: false,
      onAddBottomSiblingClick: () => {},
      onAddChildClick: () => {},
      onAddTopSiblingClick: () => {},
      onDeleteClick: () => {},
      onAddLinkAttachementClick: () => {},
      onAddFileAttachementClick: () => {},
    },
    menuRef: React.createRef(),
  }),
};
